/**
 * Load Radar Color Constants
 *
 * Centralized color token system for Load Radar components.
 * Uses canonical Numeo DS slate-based Tailwind tokens — no raw hex values.
 */

export const LOAD_RADAR_COLORS = {
    // Backgrounds
    page: 'bg-slate-50 dark:bg-slate-950',
    card: 'bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700',
    cardActive: 'bg-white dark:bg-slate-900 border-green-200 dark:border-green-800/50',
    input: 'bg-white dark:bg-slate-900 border-slate-300 dark:border-slate-600 text-slate-900 dark:text-slate-100 placeholder:text-slate-400 dark:placeholder:text-slate-500',
    inputFocus: 'focus:ring-blue-600 dark:focus:ring-blue-500 focus:border-blue-600',

    // Borders & dividers
    border: 'border-slate-200 dark:border-slate-700',
    divider: 'border-slate-200 dark:border-slate-700',

    // Text
    heading: 'text-slate-900 dark:text-slate-100',
    body: 'text-slate-700 dark:text-slate-300',
    label: 'text-slate-600 dark:text-slate-400',
    muted: 'text-slate-500 dark:text-slate-400',
    sectionHeader: 'text-slate-500 dark:text-slate-400',

    // Status badges
    status: {
        active: 'text-green-700 dark:text-green-400',
        paused: 'text-slate-500 dark:text-slate-400',
        pausedDot: 'bg-slate-300 dark:bg-slate-600',
    },

    // Notification channel badges
    badge: {
        enabled: 'text-blue-600 dark:text-blue-400',
        disabled: 'text-slate-400 dark:text-slate-600',
        count: 'text-slate-500 dark:text-slate-400',
    },

    // Buttons
    btn: {
        primary: 'bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 text-white',
        enable: 'bg-green-50 text-green-700 hover:bg-green-100 dark:bg-green-900/20 dark:text-green-400 dark:hover:bg-green-900/30',
        disable: 'bg-red-50 text-red-700 hover:bg-red-100 dark:bg-red-900/20 dark:text-red-400 dark:hover:bg-red-900/30',
        edit: 'bg-slate-50 text-slate-600 hover:bg-slate-100 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700',
        test: 'bg-blue-50 text-blue-600 hover:bg-blue-100 dark:bg-blue-900/20 dark:text-blue-400 dark:hover:bg-blue-900/30',
        delete: 'bg-slate-50 text-red-600 hover:bg-red-50 dark:bg-transparent dark:text-red-400 dark:hover:bg-red-900/20',
        cancel: 'text-slate-600 hover:bg-slate-50 dark:text-slate-400 dark:hover:bg-slate-800',
    },

    // Chip/pill selection states (equipment, sources, intervals)
    chip: {
        selected: 'bg-blue-50 border-blue-300 text-blue-700 dark:bg-blue-900/20 dark:border-blue-700 dark:text-blue-300',
        unselected:
            'bg-white border-slate-200 text-slate-600 hover:bg-slate-50 dark:bg-slate-900 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800',
        disconnected:
            'bg-slate-50 border-slate-200 text-slate-400 hover:bg-slate-100 dark:bg-slate-900 dark:border-slate-700 dark:text-slate-500 dark:hover:bg-slate-800',
    },

    // Alerts & toasts
    alert: {
        error: 'bg-red-50 border-red-200 text-red-700 dark:bg-red-900/20 dark:border-red-800 dark:text-red-300',
        info: 'bg-blue-50 border-blue-200 text-blue-700 dark:bg-blue-900/20 dark:border-blue-800 dark:text-blue-300',
    },

    // Skeleton loading
    skeleton: {
        base: 'bg-slate-200 dark:bg-slate-800',
        lighter: 'bg-slate-100 dark:bg-slate-700',
    },

    // Empty state
    emptyIcon: 'bg-blue-50 dark:bg-blue-900/20',

    // Telegram connect states
    telegram: {
        connected: 'bg-green-50 border-green-200 dark:bg-green-900/20 dark:border-green-800',
        connectedText: 'text-green-700 dark:text-green-300',
        connectedDot: 'text-green-600 dark:text-green-400',
        container: 'bg-slate-50 border-slate-200 dark:bg-slate-800 dark:border-slate-700',
        bodyText: 'text-slate-600 dark:text-slate-400',
        waiting: 'text-amber-600 dark:text-amber-400',
    },

    // DS Modal override (replaces MUI Dialog)
    dialog: {
        paper: 'dark:bg-slate-900',
        title: 'text-slate-900 dark:text-slate-100',
    },

    // Checkbox
    checkbox: 'border-slate-300 text-blue-600 focus:ring-blue-600 dark:border-slate-600 dark:bg-slate-900',
    checkboxLabel: 'text-slate-700 dark:text-slate-300',

    // MUI Autocomplete override for dark mode
    autocomplete: [
        '[&_.MuiOutlinedInput-root]:rounded-lg',
        '[&_.MuiOutlinedInput-root]:bg-white [&_.MuiOutlinedInput-root]:dark:bg-slate-900',
        '[&_fieldset]:border-slate-300 [&_fieldset]:dark:border-slate-600',
        '[&_.MuiOutlinedInput-root:hover_fieldset]:border-blue-600 [&_.MuiOutlinedInput-root:hover_fieldset]:dark:border-blue-500',
        '[&_.MuiOutlinedInput-root.Mui-focused_fieldset]:border-blue-600 [&_.MuiOutlinedInput-root.Mui-focused_fieldset]:dark:border-blue-500',
        '[&_.MuiOutlinedInput-input]:text-slate-900 [&_.MuiOutlinedInput-input]:dark:text-slate-100',
        '[&_.MuiOutlinedInput-input::placeholder]:text-slate-400 [&_.MuiOutlinedInput-input::placeholder]:dark:text-slate-500',
        '[&_.MuiChip-root]:bg-blue-50 [&_.MuiChip-root]:text-blue-700 [&_.MuiChip-root]:border-blue-200',
        '[&_.MuiChip-root]:dark:bg-blue-900/20 [&_.MuiChip-root]:dark:text-blue-300 [&_.MuiChip-root]:dark:border-blue-800',
        '[&_.MuiChip-deleteIcon]:text-blue-400 [&_.MuiChip-deleteIcon]:dark:text-blue-500',
        '[&_.MuiChip-deleteIcon:hover]:text-blue-600 [&_.MuiChip-deleteIcon:hover]:dark:text-blue-300',
        '[&_.MuiSvgIcon-root]:text-slate-400 [&_.MuiSvgIcon-root]:dark:text-slate-500',
    ].join(' '),

    autocompleteDropdown: {
        paper: 'dark:bg-slate-800 dark:border dark:border-slate-700',
        option: 'dark:text-slate-200 dark:hover:bg-slate-700',
    },
} as const
